import { requestStatusMap } from "@/lib/status-mapping";
import { type ElementType } from "react";
import { HelpCircle, Heart, Monitor, Receipt, Wrench, Eye, EyeOff, Lock, Users } from "lucide-react";
import {
  CARE_TYPE_LABELS,
  CONTACT_METHOD_LABELS,
  REQUEST_CATEGORY_LABELS,
  REQUEST_STATUS_OPTIONS,
  REQUEST_CATEGORY_OPTIONS,
  VISIBILITY_OPTIONS,
  VISIBILITY_LABELS,
} from "@/types/requests";
import type { StatusBadgeVariant } from "@/components/ui/status-badge";
import type { ChipTone } from "@/components/ui/chips";

const REQUEST_TYPE_ICON_BY_VALUE: Record<string, ElementType> = {
  care: Heart,
  facility: Wrench,
  admin: Monitor,
  reimbursement: Receipt,
  other: HelpCircle,
};

const REQUEST_TYPE_STYLE_BY_VALUE: Record<string, { iconClass: string; badgeClass: string }> = {
  care: {
    iconClass: "text-attention bg-attention-muted/40 border-attention/30",
    badgeClass: "text-attention border-attention/30 bg-attention-muted/30",
  },
  facility: {
    iconClass: "text-warning bg-warning-muted/40 border-warning/30",
    badgeClass: "text-warning border-warning/30 bg-warning-muted/30",
  },
  admin: {
    iconClass: "text-info bg-info-muted/40 border-info/30",
    badgeClass: "text-info border-info/30 bg-info-muted/30",
  },
  reimbursement: {
    iconClass: "text-success-600 bg-success-50 border-success-200 dark:text-success-400 dark:bg-success-700/30 dark:border-success-700",
    badgeClass: "text-success-600 border-success-200 bg-success-50 dark:text-success-400 dark:border-success-700 dark:bg-success-700/30",
  },
  other: {
    iconClass: "text-muted-foreground bg-muted/40 border-muted/50",
    badgeClass: "text-muted-foreground border-muted/50 bg-muted/40",
  },
};

const REQUEST_TYPE_DESCRIPTION_BY_VALUE: Record<string, string> = {
  care: "Prayer, visits, counseling, benevolence",
  facility: "Maintenance, setup, and building requests",
  admin: "Administrative and operational tasks",
  reimbursement: "Submit expense reimbursement",
  other: "General or miscellaneous requests",
};

const REQUEST_VISIBILITY_ICON_BY_VALUE: Record<string, ElementType> = {
  private: Lock,
  restricted: EyeOff,
  clergy_only: Lock,
  ministry_team: Users,
  prayer_list: Heart,
  normal: Eye,
};

type RequestVisibilityLevel = string;

export interface RequestTypeMetadata {
  label: string;
  icon: ElementType;
  iconClass: string;
  badgeClass: string;
  description: string;
}

export interface RequestVisibilityMetadata {
  label: string;
  icon: ElementType;
  description: string;
}

const normalizeLabel = (value: string) =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const formatRequestTypeLabel = (type?: string | null) => {
  if (!type) return "Request";
  return REQUEST_CATEGORY_LABELS[type] || normalizeLabel(type);
};

export const getRequestTypeMetadata = (type?: string | null): RequestTypeMetadata => {
  const normalizedType = type || "other";
  const label = formatRequestTypeLabel(normalizedType);
  const normalizedLabel = normalizeLabel(normalizedType);
  const description = REQUEST_TYPE_DESCRIPTION_BY_VALUE[normalizedType]
    || REQUEST_TYPE_OPTIONS.find((option) => option.value === normalizedType)?.label
    || `Track a ${label.toLowerCase()} request.`;

  return {
    label: label || normalizedLabel,
    icon: REQUEST_TYPE_ICON_BY_VALUE[normalizedType] || HelpCircle,
    iconClass: REQUEST_TYPE_STYLE_BY_VALUE[normalizedType]?.iconClass || "text-muted-foreground bg-muted/40 border-muted/50",
    badgeClass: REQUEST_TYPE_STYLE_BY_VALUE[normalizedType]?.badgeClass || "text-muted-foreground border-muted/50 bg-muted/40",
    description,
  };
};

export const getRequestTypeIcon = (type?: string | null): ElementType => {
  if (!type) return HelpCircle;
  return REQUEST_TYPE_ICON_BY_VALUE[type] || HelpCircle;
};

export const getRequestVisibilityMetadata = (visibility?: string | null): RequestVisibilityMetadata => {
  const normalizedVisibility = visibility || "normal";
  const label = formatVisibilityLabel(normalizedVisibility);
  const visibilityOption = VISIBILITY_OPTIONS.find((option) => option.value === normalizedVisibility);

  return {
    label,
    icon: REQUEST_VISIBILITY_ICON_BY_VALUE[normalizedVisibility] || Eye,
    description: visibilityOption?.description || "Shared with the team based on your role permissions.",
  };
};

export const formatRequestStatusLabel = (status?: string | null) => {
  if (!status) return "Unknown";
  return (
    requestStatusMap[status as keyof typeof requestStatusMap]?.label ||
    REQUEST_STATUS_OPTIONS.find((option) => option.value === status)?.label ||
    normalizeLabel(status)
  );
};

export const getRequestStatusDisplay = (status: string): {
  label: string;
  variant: StatusBadgeVariant;
  tone: ChipTone;
} => {
  const config = requestStatusMap[status as keyof typeof requestStatusMap];
  return {
    label: formatRequestStatusLabel(status),
    variant: config?.variant ?? "neutral",
    tone: config?.tone ?? "neutral",
  };
};

export const formatCareTypeLabel = (careType?: string | null) => {
  if (!careType) return "Unknown";
  return CARE_TYPE_LABELS[careType] || normalizeLabel(careType);
};

export const formatContactMethodLabel = (contactMethod?: string | null) => {
  if (!contactMethod) return "Unknown";
  return CONTACT_METHOD_LABELS[contactMethod] || normalizeLabel(contactMethod);
};

export const formatVisibilityLabel = (visibility?: string | null) => {
  if (!visibility) return "Normal";
  return VISIBILITY_LABELS[visibility] || normalizeLabel(visibility);
};

export const isRequestOpenStatus = (status: string) => {
  return status === "open" || status === "in_progress" || status === "waiting";
};
